/**
 * API Client - Cliente para PostgREST
 * Conecta con el backend PostgreSQL via PostgREST
 */

import { getApiUrl } from '@/lib/config';

// En móvil usar dominio/servidor configurado, en web usar proxy relativo
// Use a getter so the URL is re-evaluated on each request (allows runtime override via localStorage)
const apiUrl = () => getApiUrl();
export const DATA_CHANGE_EVENT = 'athenea:data-change';
export const DATA_CHANGE_CHANNEL = 'athenea-data-change';

const dataChangeChannel =
  typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel(DATA_CHANGE_CHANNEL)
    : null;

const emitDataChange = (detail: { table: string; action: 'insert' | 'update' | 'upsert' | 'delete' }) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(DATA_CHANGE_EVENT, { detail }));
  dataChangeChannel?.postMessage(detail);
};

type FilterOperator = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'ilike' | 'is' | 'in' | 'contains' | 'containedBy' | 'overlaps';

interface Filter {
  column: string;
  operator: FilterOperator;
  value: any;
}

interface OrderBy {
  column: string;
  ascending: boolean;
}

interface QueryResult<T> {
  data: T[] | null;
  error: Error | null;
  count?: number;
}

interface SingleResult<T> {
  data: T | null;
  error: Error | null;
}

interface MutationResult<T> {
  data: T[] | null;
  error: Error | null;
}

class QueryBuilder<T = any> {
  private table: string;
  private selectColumns: string = '*';
  private filters: Filter[] = [];
  private orFilters: string[] = [];
  private orderByClause: OrderBy | null = null;
  private limitCount: number | null = null;
  private offsetCount: number | null = null;
  private rangeStart: number | null = null;
  private rangeEnd: number | null = null;
  private isSingle: boolean = false;
  private maybeSingle: boolean = false;
  private countOption: 'exact' | 'planned' | 'estimated' | null = null;
  private headers: Record<string, string> = {};

  constructor(table: string) {
    this.table = table;
  }

  select(columns: string = '*', options?: { count?: 'exact' | 'planned' | 'estimated' }): this {
    this.selectColumns = columns;
    if (options?.count) {
      this.countOption = options.count;
    }
    return this;
  }

  // Filter methods
  eq(column: string, value: any): this {
    this.filters.push({ column, operator: 'eq', value });
    return this;
  }

  neq(column: string, value: any): this {
    this.filters.push({ column, operator: 'neq', value });
    return this;
  }

  gt(column: string, value: any): this {
    this.filters.push({ column, operator: 'gt', value });
    return this;
  }

  gte(column: string, value: any): this {
    this.filters.push({ column, operator: 'gte', value });
    return this;
  }

  lt(column: string, value: any): this {
    this.filters.push({ column, operator: 'lt', value });
    return this;
  }

  lte(column: string, value: any): this {
    this.filters.push({ column, operator: 'lte', value });
    return this;
  }

  like(column: string, value: string): this {
    this.filters.push({ column, operator: 'like', value });
    return this;
  }

  ilike(column: string, value: string): this {
    this.filters.push({ column, operator: 'ilike', value });
    return this;
  }

  is(column: string, value: null | boolean): this {
    this.filters.push({ column, operator: 'is', value });
    return this;
  }

  in(column: string, values: any[]): this {
    this.filters.push({ column, operator: 'in', value: values });
    return this;
  }

  contains(column: string, value: any[]): this {
    this.filters.push({ column, operator: 'contains', value });
    return this;
  }

  containedBy(column: string, value: any[]): this {
    this.filters.push({ column, operator: 'containedBy', value });
    return this;
  }

  overlaps(column: string, value: any[]): this {
    this.filters.push({ column, operator: 'overlaps', value });
    return this;
  }

  // OR filter - accepts string or string[]
  or(conditions: string | string[]): this {
    if (typeof conditions === 'string') {
      this.orFilters = [conditions];
    } else {
      this.orFilters = conditions;
    }
    return this;
  }

  // Ordering
  order(column: string, options?: { ascending?: boolean }): this {
    this.orderByClause = {
      column,
      ascending: options?.ascending ?? true,
    };
    return this;
  }

  // Pagination
  limit(count: number): this {
    this.limitCount = count;
    return this;
  }

  range(start: number, end: number): this {
    this.rangeStart = start;
    this.rangeEnd = end;
    return this;
  }

  // Single row
  single(): QueryBuilder<T> {
    this.isSingle = true;
    return this;
  }

  maybeSingleRow(): QueryBuilder<T> {
    this.maybeSingle = true;
    return this;
  }

  // Build URL with filters
  private buildUrl(): string {
    const params = new URLSearchParams();

    // Select columns
    params.set('select', this.selectColumns);

    // Filters
    for (const filter of this.filters) {
      let value: string;
      switch (filter.operator) {
        case 'eq':
          value = `eq.${filter.value}`;
          break;
        case 'neq':
          value = `neq.${filter.value}`;
          break;
        case 'gt':
          value = `gt.${filter.value}`;
          break;
        case 'gte':
          value = `gte.${filter.value}`;
          break;
        case 'lt':
          value = `lt.${filter.value}`;
          break;
        case 'lte':
          value = `lte.${filter.value}`;
          break;
        case 'like':
          value = `like.${filter.value}`;
          break;
        case 'ilike':
          value = `ilike.${filter.value}`;
          break;
        case 'is':
          value = `is.${filter.value}`;
          break;
        case 'in':
          value = `in.(${filter.value.join(',')})`;
          break;
        case 'contains':
          value = `cs.{${filter.value.join(',')}}`;
          break;
        case 'containedBy':
          value = `cd.{${filter.value.join(',')}}`;
          break;
        case 'overlaps':
          value = `ov.{${filter.value.join(',')}}`;
          break;
        default:
          value = `eq.${filter.value}`;
      }
      params.append(filter.column, value);
    }

    if (this.orFilters.length > 0) {
      const orValue = this.orFilters.length === 1 ? this.orFilters[0] : this.orFilters.join(',');
      params.set('or', `(${orValue})`);
    }

    // Order
    if (this.orderByClause) {
      const direction = this.orderByClause.ascending ? 'asc' : 'desc';
      params.set('order', `${this.orderByClause.column}.${direction}`);
    }

    // Limit
    if (this.limitCount !== null) {
      params.set('limit', this.limitCount.toString());
    }

    // Offset (for range)
    if (this.rangeStart !== null) {
      params.set('offset', this.rangeStart.toString());
      if (this.rangeEnd !== null) {
        params.set('limit', (this.rangeEnd - this.rangeStart + 1).toString());
      }
    }

    return `${apiUrl()}/${this.table}?${params.toString()}`;
  }

  // Execute query
  async then<TResult1 = QueryResult<T> | SingleResult<T>>(
    resolve: (value: QueryResult<T> | SingleResult<T>) => TResult1
  ): Promise<TResult1> {
    try {
      const url = this.buildUrl();
        const headers: Record<string, string> = {
          'Accept': 'application/json',
          ...this.headers,
        };
        headers['Cache-Control'] = 'no-store';
        headers['Pragma'] = 'no-cache';

      // Request count header if needed
      if (this.countOption) {
        headers['Prefer'] = `count=${this.countOption}`;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
        const response = await fetch(url, { headers, signal: controller.signal, cache: 'no-store' });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error ${response.status}`);
      }

      const data = await response.json();

      // Get count from headers if requested
      let count: number | undefined;
      if (this.countOption) {
        const contentRange = response.headers.get('Content-Range');
        if (contentRange) {
          const match = contentRange.match(/\/(\d+)/);
          if (match) {
            count = parseInt(match[1], 10);
          }
        }
      }

      if (this.isSingle) {
        if (!data || data.length === 0) {
          return resolve({ data: null, error: new Error('No rows found') } as SingleResult<T>);
        }
        return resolve({ data: data[0], error: null } as SingleResult<T>);
      }

      if (this.maybeSingle) {
        return resolve({ data: data?.[0] ?? null, error: null } as SingleResult<T>);
      }

      return resolve({ data, error: null, count } as QueryResult<T>);
    } catch (error: any) {
      if (this.isSingle || this.maybeSingle) {
        return resolve({ data: null, error } as SingleResult<T>);
      }
      return resolve({ data: null, error } as QueryResult<T>);
    }
  }
}

class InsertBuilder<T = any> {
  private table: string;
  private data: any;
  private returnData: boolean = false;
  private selectColumns: string = '*';
  private onConflictColumn: string | null = null;
  private ignoreDuplicates: boolean = false;
  private isSingle: boolean = false;

  constructor(table: string, data: any) {
    this.table = table;
    this.data = data;
  }

  select(columns: string = '*'): this {
    this.returnData = true;
    this.selectColumns = columns;
    return this;
  }

  onConflict(column: string): this {
    this.onConflictColumn = column;
    return this;
  }

  ignoreDuplicatesOnConflict(): this {
    this.ignoreDuplicates = true;
    return this;
  }

  single(): this {
    this.returnData = true;
    this.isSingle = true;
    return this;
  }

  async then<TResult1 = MutationResult<T>>(
    resolve: (value: MutationResult<T>) => TResult1
  ): Promise<TResult1> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      if (this.returnData) {
        headers['Prefer'] = 'return=representation';
      }

      if (this.onConflictColumn && this.ignoreDuplicates) {
        headers['Prefer'] = `${headers['Prefer'] || ''},resolution=ignore-duplicates`;
      }

      let url = `${apiUrl()}/${this.table}`;
      if (this.returnData) {
        url += `?select=${this.selectColumns}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(this.data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error ${response.status}`);
      }

      if (this.returnData) {
        const data = await response.json();
        emitDataChange({ table: this.table, action: 'insert' });
        // Si single() fue llamado, retornar el primer elemento
        const result = this.isSingle && Array.isArray(data) ? data[0] : data;
        return resolve({ data: result, error: null });
      }

      emitDataChange({ table: this.table, action: 'insert' });
      return resolve({ data: null, error: null });
    } catch (error: any) {
      return resolve({ data: null, error });
    }
  }
}

class UpdateBuilder<T = any> {
  private table: string;
  private data: any;
  private filters: Filter[] = [];
  private returnData: boolean = false;
  private selectColumns: string = '*';
  private isSingle: boolean = false;

  constructor(table: string, data: any) {
    this.table = table;
    this.data = data;
  }

  eq(column: string, value: any): this {
    this.filters.push({ column, operator: 'eq', value });
    return this;
  }

  neq(column: string, value: any): this {
    this.filters.push({ column, operator: 'neq', value });
    return this;
  }

  in(column: string, values: any[]): this {
    this.filters.push({ column, operator: 'in', value: values });
    return this;
  }

  is(column: string, value: null | boolean): this {
    this.filters.push({ column, operator: 'is', value });
    return this;
  }

  lt(column: string, value: any): this {
    this.filters.push({ column, operator: 'lt', value });
    return this;
  }

  lte(column: string, value: any): this {
    this.filters.push({ column, operator: 'lte', value });
    return this;
  }

  gt(column: string, value: any): this {
    this.filters.push({ column, operator: 'gt', value });
    return this;
  }

  gte(column: string, value: any): this {
    this.filters.push({ column, operator: 'gte', value });
    return this;
  }

  select(columns: string = '*'): this {
    this.returnData = true;
    this.selectColumns = columns;
    return this;
  }

  single(): this {
    this.returnData = true;
    this.isSingle = true;
    return this;
  }

  private buildUrl(): string {
    const params = new URLSearchParams();

    if (this.returnData) {
      params.set('select', this.selectColumns);
    }

    for (const filter of this.filters) {
      let value: string;
      switch (filter.operator) {
        case 'eq':
          value = `eq.${filter.value}`;
          break;
        case 'neq':
          value = `neq.${filter.value}`;
          break;
        case 'in':
          value = `in.(${filter.value.join(',')})`;
          break;
        case 'is':
          value = `is.${filter.value}`;
          break;
        case 'lt':
          value = `lt.${filter.value}`;
          break;
        case 'lte':
          value = `lte.${filter.value}`;
          break;
        case 'gt':
          value = `gt.${filter.value}`;
          break;
        case 'gte':
          value = `gte.${filter.value}`;
          break;
        default:
          value = `eq.${filter.value}`;
      }
      params.append(filter.column, value);
    }

    return `${apiUrl()}/${this.table}?${params.toString()}`;
  }

  async then<TResult1 = MutationResult<T>>(
    resolve: (value: MutationResult<T>) => TResult1
  ): Promise<TResult1> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      if (this.returnData) {
        headers['Prefer'] = 'return=representation';
      }

      const response = await fetch(this.buildUrl(), {
        method: 'PATCH',
        headers,
        body: JSON.stringify(this.data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error ${response.status}`);
      }

      if (this.returnData) {
        const data = await response.json();
        emitDataChange({ table: this.table, action: 'update' });
        const result = this.isSingle && Array.isArray(data) ? data[0] : data;
        return resolve({ data: result, error: null });
      }

      emitDataChange({ table: this.table, action: 'update' });
      return resolve({ data: null, error: null });
    } catch (error: any) {
      return resolve({ data: null, error });
    }
  }
}

class UpsertBuilder<T = any> {
  private table: string;
  private data: any;
  private returnData: boolean = false;
  private selectColumns: string = '*';
  private onConflictColumns: string[] = [];
  private isSingle: boolean = false;

  constructor(table: string, data: any) {
    this.table = table;
    this.data = data;
  }

  onConflict(columns: string): this {
    this.onConflictColumns = columns.split(',').map(c => c.trim());
    return this;
  }

  select(columns: string = '*'): this {
    this.returnData = true;
    this.selectColumns = columns;
    return this;
  }

  single(): this {
    this.returnData = true;
    this.isSingle = true;
    return this;
  }

  async then<TResult1 = MutationResult<T>>(
    resolve: (value: MutationResult<T>) => TResult1
  ): Promise<TResult1> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Prefer': 'resolution=merge-duplicates',
      };

      if (this.returnData) {
        headers['Prefer'] += ',return=representation';
      }

      let url = `${apiUrl()}/${this.table}`;
      const params = new URLSearchParams();

      if (this.returnData) {
        params.set('select', this.selectColumns);
      }

      if (this.onConflictColumns.length > 0) {
        params.set('on_conflict', this.onConflictColumns.join(','));
      }

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(this.data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error ${response.status}`);
      }

      if (this.returnData) {
        const data = await response.json();
        emitDataChange({ table: this.table, action: 'upsert' });
        const result = this.isSingle && Array.isArray(data) ? data[0] : data;
        return resolve({ data: result, error: null });
      }

      emitDataChange({ table: this.table, action: 'upsert' });
      return resolve({ data: null, error: null });
    } catch (error: any) {
      return resolve({ data: null, error });
    }
  }
}

class DeleteBuilder<T = any> {
  private table: string;
  private filters: Filter[] = [];
  private returnData: boolean = false;
  private selectColumns: string = '*';

  constructor(table: string) {
    this.table = table;
  }

  eq(column: string, value: any): this {
    this.filters.push({ column, operator: 'eq', value });
    return this;
  }

  neq(column: string, value: any): this {
    this.filters.push({ column, operator: 'neq', value });
    return this;
  }

  in(column: string, values: any[]): this {
    this.filters.push({ column, operator: 'in', value: values });
    return this;
  }

  is(column: string, value: null | boolean): this {
    this.filters.push({ column, operator: 'is', value });
    return this;
  }

  select(columns: string = '*'): this {
    this.returnData = true;
    this.selectColumns = columns;
    return this;
  }

  private buildUrl(): string {
    const params = new URLSearchParams();

    if (this.returnData) {
      params.set('select', this.selectColumns);
    }

    for (const filter of this.filters) {
      let value: string;
      switch (filter.operator) {
        case 'eq':
          value = `eq.${filter.value}`;
          break;
        case 'neq':
          value = `neq.${filter.value}`;
          break;
        case 'in':
          value = `in.(${filter.value.join(',')})`;
          break;
        case 'is':
          value = `is.${filter.value}`;
          break;
        default:
          value = `eq.${filter.value}`;
      }
      params.append(filter.column, value);
    }

    return `${apiUrl()}/${this.table}?${params.toString()}`;
  }

  async then<TResult1 = MutationResult<T>>(
    resolve: (value: MutationResult<T>) => TResult1
  ): Promise<TResult1> {
    try {
      const headers: Record<string, string> = {
        'Accept': 'application/json',
      };

      if (this.returnData) {
        headers['Prefer'] = 'return=representation';
      }

      const response = await fetch(this.buildUrl(), {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error ${response.status}`);
      }

      if (this.returnData) {
        const data = await response.json();
        emitDataChange({ table: this.table, action: 'delete' });
        return resolve({ data, error: null });
      }

      emitDataChange({ table: this.table, action: 'delete' });
      return resolve({ data: null, error: null });
    } catch (error: any) {
      return resolve({ data: null, error });
    }
  }
}

class RpcBuilder<T = any> {
  private functionName: string;
  private params: any;

  constructor(functionName: string, params?: any) {
    this.functionName = functionName;
    this.params = params;
  }

  async then<TResult1 = { data: T | null; error: Error | null }>(
    resolve: (value: { data: T | null; error: Error | null }) => TResult1
  ): Promise<TResult1> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${apiUrl()}/rpc/${this.functionName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(this.params || {}),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error ${response.status}`);
      }

      const data = await response.json();
      return resolve({ data, error: null });
    } catch (error: any) {
      return resolve({ data: null, error });
    }
  }
}

class TableClient {
  private table: string;

  constructor(table: string) {
    this.table = table;
  }

  select(columns: string = '*', options?: { count?: 'exact' | 'planned' | 'estimated' }): QueryBuilder {
    return new QueryBuilder(this.table).select(columns, options);
  }

  insert(data: any | any[]): InsertBuilder {
    return new InsertBuilder(this.table, Array.isArray(data) ? data : [data]);
  }

  update(data: any): UpdateBuilder {
    return new UpdateBuilder(this.table, data);
  }

  upsert(data: any | any[], options?: { onConflict?: string }): UpsertBuilder {
    const builder = new UpsertBuilder(this.table, Array.isArray(data) ? data : [data]);
    if (options?.onConflict) {
      builder.onConflict(options.onConflict);
    }
    return builder;
  }

  delete(): DeleteBuilder {
    return new DeleteBuilder(this.table);
  }
}

class ApiClient {
  from(table: string): TableClient {
    return new TableClient(table);
  }

  rpc<T = any>(functionName: string, params?: any): RpcBuilder<T> {
    return new RpcBuilder<T>(functionName, params);
  }
}

// Helper para obtener el usuario actual desde localStorage
export const getCurrentUser = (): { id: string; email: string; full_name: string | null } | null => {
  try {
    const sessionData = localStorage.getItem('athenea_session');
    if (!sessionData) return null;

    const session = JSON.parse(sessionData);
    if (session.expires_at < Date.now()) {
      localStorage.removeItem('athenea_session');
      return null;
    }

    return session.user;
  } catch {
    return null;
  }
};

// Clase para manejar polling como alternativa a realtime
class PollingSubscription {
  private intervalId: NodeJS.Timeout | null = null;
  private callback: () => void;
  private intervalMs: number;

  constructor(callback: () => void, intervalMs: number = 5000) {
    this.callback = callback;
    this.intervalMs = intervalMs;
    // Auto-start for convenience
    this.start();
  }

  start(): this {
    if (this.intervalId) return this;
    this.intervalId = setInterval(this.callback, this.intervalMs);
    return this;
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // Alias para compatibilidad con código que espera unsubscribe()
  unsubscribe(): void {
    this.stop();
  }
}

// Función para crear suscripciones basadas en polling
export const createPollingSubscription = (
  callback: () => void,
  intervalMs: number = 5000
): PollingSubscription => {
  return new PollingSubscription(callback, intervalMs);
};

// Export singleton instance
export const api = new ApiClient();

// Funcion para establecer contexto de auditoria antes de operaciones
export const setAuditContext = async (userId: string, userRole: string, userName: string): Promise<void> => {
  try {
    await api.rpc('set_audit_context', {
      p_user_id: userId,
      p_user_role: userRole,
      p_user_name: userName,
    });
  } catch (error) {
    console.warn('Could not set audit context:', error);
  }
};

// Export types for use in components
export type { QueryResult, SingleResult, MutationResult, PollingSubscription };
